import { Layout } from "@/components/Layout";
import { useSearchParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CheckCircle2, Crown, Star, ArrowRight, Mail, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { logError } from "@/lib/log";
import { EnquiryDialog } from "@/components/EnquiryDialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const MembershipSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const [status, setStatus] = useState("activating");
  const [membership, setMembership] = useState(null);
  const [attempts, setAttempts] = useState(0);
  const maxAttempts = 5;

  const isLoggedIn = localStorage.getItem("customer_logged_in") === "true";

  useEffect(() => {
    const activateMembership = async () => {
      if (!sessionId || !isLoggedIn) {
        setStatus("error");
        return;
      }

      try {
        const response = await axios.post(
          `${API}/memberships/activate?session_id=${sessionId}`,
          {}
        );
        setMembership(response.data);
        setStatus("success");
      } catch (error) {
        logError("Activation error:", error);
        if (attempts < maxAttempts) {
          setTimeout(() => setAttempts(prev => prev + 1), 2000);
        } else {
          setStatus("error");
        }
      }
    };

    if (status === "activating") {
      activateMembership();
    }
  }, [sessionId, isLoggedIn, attempts, status]);

  return (
    <Layout>
      <section className="py-24 md:py-32 bg-[#F9FAF9] min-h-screen">
        <div className="container-clean">
          <div className="max-w-2xl mx-auto">
            {status === "activating" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="text-center"
              >
                <Loader2 className="w-16 h-16 text-[#7CB342] animate-spin mx-auto mb-6" />
                <h1 className="text-3xl font-bold text-[#1A2E12] mb-4">
                  Activating Your Membership
                </h1>
                <p className="text-[#5C6B57]">
                  Please wait while we set up your membership...
                </p>
              </motion.div>
            )}

            {status === "success" && membership && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <Card className="bg-white border-[#E2E8E0] rounded-2xl shadow-natural overflow-hidden">
                  <div className={`p-8 text-center ${
                    membership.plan === "platinum" 
                      ? "bg-gradient-to-r from-[#2D5016] to-[#1A2E12]" 
                      : "bg-gradient-to-r from-[#7CB342] to-[#558B2F]"
                  }`}>
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ delay: 0.2, type: "spring" }}
                    >
                      {membership.plan === "platinum" ? (
                        <Crown className="w-20 h-20 text-white mx-auto mb-4" strokeWidth={1.5} />
                      ) : (
                        <Star className="w-20 h-20 text-white mx-auto mb-4" strokeWidth={1.5} />
                      )}
                    </motion.div>
                    <h1 className="text-3xl font-bold text-white mb-2" data-testid="success-title">
                      Welcome to {membership.plan_name}!
                    </h1>
                    <p className="text-white/80">
                      Your membership is now active
                    </p>
                  </div>
                  
                  <CardContent className="p-8">
                    <div className="space-y-6">
                      <div className="bg-[#E8F5E9] rounded-xl p-6">
                        <h3 className="font-semibold text-[#1A2E12] mb-4">Your Membership Benefits</h3>
                        <ul className="space-y-3">
                          <li className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-[#7CB342] mt-0.5" strokeWidth={1.5} />
                            <span className="text-[#5C6B57]">
                              {membership.priority_bookings_remaining === 999 
                                ? "Unlimited priority bookings" 
                                : `${membership.priority_bookings_remaining} priority bookings remaining`
                              }
                            </span>
                          </li>
                          <li className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-[#7CB342] mt-0.5" strokeWidth={1.5} />
                            <span className="text-[#5C6B57]">
                              {membership.free_steam_cleans_remaining} free steam clean 
                              ({membership.steam_clean_areas.join(" & ")})
                            </span>
                          </li>
                          <li className="flex items-start gap-3">
                            <CheckCircle2 className="w-5 h-5 text-[#7CB342] mt-0.5" strokeWidth={1.5} />
                            <span className="text-[#5C6B57]">
                              {membership.discount_percentage}% off all bookings
                            </span>
                          </li>
                        </ul>
                      </div>

                      <div className="bg-[#F9FAF9] rounded-xl p-6">
                        <h3 className="font-semibold text-[#1A2E12] mb-3">Membership Details</h3>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-[#5C6B57]">Valid until</span>
                            <span className="font-medium text-[#1A2E12]">
                              {new Date(membership.end_date).toLocaleDateString('en-GB', {
                                day: 'numeric',
                                month: 'long',
                                year: 'numeric'
                              })}
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-[#5C6B57]">Amount Paid</span>
                            <span className="font-medium text-[#1A2E12]">£{membership.price.toFixed(2)}</span>
                          </div>
                        </div>
                      </div>

                      <div className="flex flex-col sm:flex-row gap-4">
                        <Link to="/account" className="flex-1">
                          <Button 
                            className="w-full bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full py-6"
                            data-testid="btn-account"
                          >
                            Go to My Account
                            <ArrowRight className="ml-2 w-4 h-4" />
                          </Button>
                        </Link>
                        <Link to="/booking" className="flex-1">
                          <Button 
                            variant="outline"
                            className="w-full border-[#2D5016] text-[#2D5016] rounded-full py-6"
                          >
                            Book a Clean
                          </Button>
                        </Link>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            )}

            {status === "error" && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
              >
                <Card className="bg-white border-[#E2E8E0] rounded-2xl shadow-natural p-8 text-center">
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                    <span className="text-red-600 text-2xl">!</span>
                  </div>
                  <h1 className="text-2xl font-bold text-[#1A2E12] mb-4">
                    Something Went Wrong
                  </h1>
                  <p className="text-[#5C6B57] mb-8">
                    We couldn't activate your membership. Please contact us and we'll sort it out.
                  </p>
                  <EnquiryDialog source="membership-error" subject="Membership activation issue">
                    <Button className="bg-[#2D5016] hover:bg-[#1A2E12] text-white rounded-full px-8 py-6" data-testid="membership-error-email">
                      <Mail className="mr-2 w-4 h-4" />
                      Contact Support
                    </Button>
                  </EnquiryDialog>
                </Card>
              </motion.div>
            )}
          </div>
        </div>
      </section>
    </Layout>
  );
};

export default MembershipSuccess;
